// 伺服器展開的範圍（SPEC #org-tree-ui 第 1 點、#org-tree-impact「我的設備」）：組織樹的節點、委派、檢視、設備群組。
// 範圍鍵：
//   node:<org_id>:<node_id>          經本組織（P1 / P2 / P5）看到的節點子樹 → GET /v1/me/devices?node_id=
//   dnode:<delegation_id>:<node_id>  經委派（P6）看到的節點子樹 → ?delegation_id=&node_id=
//   dlg:<delegation_id>              一筆委派 → ?delegation_id=
//   view:<view_id> / group:<group_id> 檢視 / 設備群組 → GET /v1/views/{v}/resolve、/v1/device-groups/{g}/resolve（逐人過濾）
// 回傳設備 key（home_id/device_ref），再從「我的設備」挑出來（每台的 actions 照舊只看「我的設備」）。
import { meTreeApi, type MeTreeItem, type Resolved } from './orgTreeApi';
import type { Plane } from './planeApi';

const REMOTE = ['node', 'dnode', 'dlg', 'view', 'group'];

export function isRemoteScope(scope: string): boolean {
  return REMOTE.includes(scope.split(':')[0]);
}

/** 範圍指到的組織（node 範圍才知道；委派 / 檢視 / 群組不指到單一組織）。 */
export function orgOfRemote(scope: string): string | null {
  const [kind, org] = scope.split(':');
  return kind === 'node' ? org : null;
}

/** 樹上的一項 → 範圍鍵（委派底下的節點用 dnode）。 */
export function scopeKeyOf(it: Pick<MeTreeItem, 'kind' | 'id' | 'delegation' | 'key' | 'home_id'>, orgId?: string): string {
  switch (it.kind) {
    case 'org':
      return 'org:' + it.id;
    case 'delegation':
      return 'dlg:' + (it.delegation?.delegation_id ?? it.id);
    case 'node':
      return it.delegation ? `dnode:${it.delegation.delegation_id}:${it.id}` : `node:${orgId ?? ''}:${it.id}`;
    case 'home':
      return 'home:' + (it.home_id ?? it.id);
    case 'sharer':
      return 'sharer:' + it.id;
    default:
      return 'all';
  }
}

async function resolveAll(fetchPage: (cursor?: string) => Promise<Resolved>): Promise<string[]> {
  const out: string[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 50; i++) {
    const r = await fetchPage(cursor);
    for (const d of r?.devices ?? []) out.push(`${d.home_id}/${d.device_ref}`);
    cursor = r?.next_cursor || undefined;
    if (!cursor) break;
  }
  return out;
}

/** 各 plane 展開再合併；全部 plane 都失敗才算失敗（範圍回「全部」）。 */
export async function fetchRemoteScope(planes: Pick<Plane, 'api'>[], scope: string): Promise<string[]> {
  const [kind, a, b] = scope.split(':');
  const one = (p: Pick<Plane, 'api'>): Promise<string[]> => {
    switch (kind) {
      case 'node':
        return meTreeApi.deviceKeys(p, { node_id: b });
      case 'dnode':
        return meTreeApi.deviceKeys(p, { delegation_id: a, node_id: b });
      case 'dlg':
        return meTreeApi.deviceKeys(p, { delegation_id: a });
      case 'view':
        return resolveAll((c) => meTreeApi.resolveView(p, a, c));
      case 'group':
        return resolveAll((c) => meTreeApi.resolveGroup(p, a, c));
    }
    return Promise.resolve([]);
  };
  const rs = await Promise.allSettled(planes.map(one));
  const ok = rs.filter((r): r is PromiseFulfilledResult<string[]> => r.status === 'fulfilled');
  if (!ok.length && rs.length) throw (rs[0] as PromiseRejectedResult).reason;
  return [...new Set(ok.flatMap((r) => r.value))];
}
